<template>
    <div class="boq-approval-setup-page hle-approval-setup-page">
        <button type="button" class="boq-approval-setup-back" @click="BackToItem">
            <i class="fa-solid fa-chevron-left"></i>
            <span>{{ $t('Back') }}</span>
        </button>

        <div class="boq-approval-setup-titlebar">
            <h1>{{$t('Approval Module.Setup')}} {{itemTypeLabel}} — {{approvalTypeLabel}}</h1>
            <div class="boq-approval-setup-actions">
                <button class="btn boq-approval-setup-btn boq-approval-setup-btn--cancel" @click="BackToItem">
                    <i class="fa-solid fa-xmark"></i>
                    <span>{{ $t('Cancel') }}</span>
                </button>
                <button class="btn boq-approval-setup-btn boq-approval-setup-btn--primary" @click="SendForApproval" :disabled="isSending">
                    <i class="fa-solid fa-envelope"></i>
                    <span>
                        {{sendButtonName}}
                        <template v-if="!isSending">— {{itemTypeLabel}}</template>
                    </span>
                </button>
            </div>
        </div>

        <div class="boq-approval-setup-summary-grid">
            <section class="boq-approval-card boq-approval-card--details">
                <h2>{{itemTypeLabel}} Details</h2>
                <div class="boq-approval-card-field">
                    <label>{{itemTypeLabel}}</label>
                    <div class="boq-approval-display-field text-capitalize">{{nameOfItem || '-'}}</div>
                </div>
            </section>

            <section class="boq-approval-card boq-approval-card--project">
                <h2>{{ $t('Approval Module.Approval') }}</h2>
                <div class="boq-approval-card-field">
                    <label>{{ $t('Status') }}</label>
                    <div class="boq-approval-display-field">{{approvalTypeLabel}}</div>
                </div>
            </section>
        </div>

        <section class="boq-approval-card boq-approval-card--content">
            <h2>Content</h2>
            <div class="boq-approval-content-grid">
                <div class="boq-approval-setup-field">
                    <label class="required-style">{{$t('Approval Module.Date End')}}</label>
                    <DatePicker v-model="dateEnd" elemId="boqreview-date-dateEnd" />
                    <small class="boq-approval-setup-note">
                        <i class="fa-solid fa-circle-info"></i>
                        <span>{{$t('Approval Module.This date is only for a soft deadline this will not affect the approval process')}}.</span>
                    </small>
                </div>

                <div class="boq-approval-setup-field boq-approval-setup-field--message">
                    <label class="required-style">{{$t('Approval Module.Message')}}</label>
                    <RichTextEditor v-model:modelValue="emailMessage" :placeHolder="$t('Approval Module.Message')" :height="'200'" />
                </div>
            </div>
        </section>

        <section class="boq-approval-card boq-approval-card--team boq-approval-reviewers">
            <div class="boq-approval-card-heading">
                <h2>Project Team</h2>
            </div>

            <div class="boq-approval-reviewers__search">
                <div class="boq-approval-search-card">
                    <div class="boq-approval-team-search-row">
                        <span class="boq-approval-team-search-label">
                            {{isExternal ? $t('Approval Module.External Users Search') : $t('Approval Module.Project Team Search')}}
                        </span>
                        <div class="boq-approval-search-control">
                            <i class="fa-solid fa-magnifying-glass"></i>
                            <Select2
                                style="width:100%"
                                indexValue="_id"
                                labelName="fullname"
                                :placeHolder="$t('Search Here')"
                                v-model:optionValue="newTypeName"
                                :onOptionClick="SelectApprover"
                                :filterQuery="isExternal ? SearchExternalUserFromProjectOrOrganization : SearchMMoserUserFromProjectOrOrganization"
                                ref="select2Approver"
                            >
                            </Select2>
                        </div>
                        <button
                            type="button"
                            class="btn boq-approval-add-btn"
                            @click="AddSelectedApprover"
                            :disabled="!CanAddSelectedApprover"
                        >
                            <span>{{ $t('Add') }}</span>
                            <i class="fa-solid fa-plus"></i>
                        </button>
                    </div>
                </div>
            </div>

            <div class="table-responsive boq-approval-table-wrap">
                <table class="table boq-approval-table">
                    <thead>
                        <tr>
                            <th>{{$t('Approval Module.Approver/Reviewer') }}</th>
                            <th>{{$t('Role') }}</th>
                            <th>{{$t('Status') }}</th>
                            <th>{{$t('Actions') }}</th>
                        </tr>
                    </thead>
                    <tbody v-if="approversList.length==0">
                        <tr>
                            <td colspan="4" class="text-center boq-approval-table__empty">
                                <i class="fa-solid fa-user-plus"></i>
                                <span>{{$t('Approval Module.Please put atleast one Approver/Reviewer') }}</span>
                            </td>
                        </tr>
                    </tbody>
                    <tbody v-else>
                        <tr v-for="(item,key) in approversList" :key="key">
                            <td>
                                <b class="text-capitalize">{{item.fullname}}</b>
                            </td>
                            <td>{{item.roleId.roleName}}</td>
                            <td>
                                <select class="form-select boq-approval-action-select" v-model.number="item.actionType" @change="HandleApproverActionChange(item)" v-if="!isApproversTypeDisabled">
                                    <option :value="1">{{$t('Approval Module.Review')}}</option>
                                    <option :value="2">{{$t('Approval Module.Approve')}}</option>
                                </select>
                                <span class="boq-approval-role-pill" v-else>
                                    {{$t('Approval Module.Approve')}}
                                </span>
                            </td>
                            <td>
                                <button class="btn boq-approval-remove-btn" @click="RemoveApprover(item._id)">
                                    <i class="fa-solid fa-xmark"></i>
                                    <span>{{$t('Remove')}}</span>
                                </button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </section>

    </div>
</template>

<script>
import Select2  from '@/components/layout/Select2.vue'
import {LinkList, ReformatDate, TextCapitalize} from '@/helpers/utilities.js'
import {mapActions, mapGetters} from 'vuex' 
import DatePicker from '@/components/layout/DatePicker.vue'
import RichTextEditor from '@/components/layout/RichTextEditor.vue'
import {SearchMMoserUserFromProjectOrOrganization, SearchExternalUserFromProjectOrOrganization ,UserSendEmailBulk} from '@/actions/projectActions.js'
import {CheckSessionMsal, GetMyUserInfo} from '@/actions/userActions.js'
import {GetHighLevelBoq, ForApprovalHighLevelBoq, GetHighLevelQuotationForApproval, SendHighLevelQuotationForApproval, GetHighLevelProject,
    GetHleNewBoqCreatorSections, GetHighLevelBoqLineItemsByBits} from '@/actions/boqActions.js'
import 'dotenv/config'
import {LocaleNameVariable} from '@/translations/i18n.js'
export default {
    name : 'HleApprovalSetup',
    mounted : async function(){
        const self = this

        self.isApproversTypeDisabled = self.isExternal
        self.ChangeActiveLink(LinkList.HIGH_LEVEL_ESTIMATES)
        let type = self.boqId ? 1 : 3
        const checkEmailSession = await CheckSessionMsal(type)
        if(!checkEmailSession){
            if(self.quotationId){
                window.location.href = process.env.VUE_APP_REST_MAIN_API + '/api/auth/token/email?type=' + type +'&returnUrl='+encodeURIComponent(window.location.href)
            }else{
                window.location.href = process.env.VUE_APP_REST_MAIN_API + '/api/auth/token/email?type=' + type +'&returnUrl='+encodeURIComponent(window.location.href)
            }
        }

        let hleProjectId = ''
        if(self.boqId){
            const getBoq = await GetHighLevelBoq(self.boqId)
            if(getBoq==400){
                self.$router.push('/unauthorized')
            }
            hleProjectId = getBoq.hleProjectId
            self.backUrl = {name : 'HleBoQBuilder', params : { id : getBoq.hleProjectId,boqId :self.boqId }}
            self.nameOfItem = getBoq.name
            await self.AddLoggedInUserAsDefaultApprover()
            
        }else if(self.qtnId){
            const getQtn = await GetHighLevelQuotationForApproval(self.qtnId)
            if(getQtn==400){
                self.$router.push('/unauthorized')
            }
            hleProjectId = getQtn.hleProjectId
            self.backUrl = {name : 'HleQuotation', params : { id: getQtn.hleProjectId, qtnId :self.qtnId }}
            self.nameOfItem = getQtn.name
            let arrayApprovers = []
            if(!self.isExternal)
            {
                arrayApprovers = getQtn.submittedBy
            }
            else
            {
                arrayApprovers = getQtn.approvedBy
            }
            self.approversList = arrayApprovers.map((u, index)=> {
                return {
                    _id : u._id,
                    fullname : u.name,
                    roleId : u.roleId,
                    actionType : self.isExternal || index==0 ? 2 : 1
                }
            })
            self.EnsureSingleApprover()
        }
        if(hleProjectId){
            const hleProject = await GetHighLevelProject(hleProjectId)
            if(hleProject && hleProject!=400){
                self.projectName = hleProject.projectName || ''
            }
        }

        const getLanguage = window.localStorage.getItem(LocaleNameVariable) || 'en'
        const projectDisplayName = self.projectName || self.nameOfItem || '-'
        const itemTypeName = self.boqId ? 'Detailed Estimate' : 'High Level Estimate'

        if(getLanguage=='en')
        {
            self.emailMessage = `
            <style>
                h1, h2, h3, h4, h5, h6 {
                    text-align: left !important;
                    margin-left: 0 !important;
                    margin-right: auto !important;
                }
            </style>
            <div style="text-align:left; font-family:Arial, sans-serif; font-size:14px; line-height:1.5; color:#000000;">
                <p style="margin:0 0 22px 0;">Hi,</p>
                <p style="margin:0 0 22px 0;">
                    We hope this message finds you well. ${itemTypeName} <b>${TextCapitalize(self.nameOfItem)}</b> for project <b>${projectDisplayName}</b> is ready for your review and approval. Your approval is required by <b>${ReformatDate(self.dateEnd)}</b>.
                </p>
                <p style="margin:0;">
                    If you have any questions or concerns, please do not hesitate to reach out.
                </p>
            </div>
            `
        }
        else
        {
            self.emailMessage = `
            我们希望您一切都好。现在可以开始审查和批准<b>${TextCapitalize(self.nameOfItem)}</b>了。您需要在<b>${ReformatDate(self.dateEnd)}</b>之前批准。
            <br/><br/>
            请注意，及时批准对于维持项目的推进和避免潜在延误至关重要。我们感谢您的合作，并期待您在<b>${ReformatDate(self.dateEnd)}</b>之前给出积极的回复。 
            <br/><br/>
            如果您有任何疑问或顾虑，请随时联系我们。
            `
        }
        
    },
    computed : {
        ...mapGetters(['userInfo']),
        itemTypeLabel(){
            return this.boqId ? this.$i18n.t('Bidding Module.Detailed Estimate') : this.$i18n.t('Bidding Module.High Level Estimate')
        },
        approvalTypeLabel(){
            return `${this.isExternal ? this.$i18n.t('Approval Module.External') : this.$i18n.t('Approval Module.Internal')} ${this.$i18n.t('Approval Module.Approval')}`
        },
        CanAddSelectedApprover(){
            const self = this
            if(!self.selectedApprover){
                return false
            }

            return self.approversList.findIndex(u=>u._id==self.selectedApprover._id)==-1
        }
    },
    components : {
        DatePicker, RichTextEditor, Select2
    },
    methods : {
        ...mapActions(['ChangeActiveLink']),
        SearchExternalUserFromProjectOrOrganization (value){
            return SearchExternalUserFromProjectOrOrganization(value,'', 1)
        },
        SearchMMoserUserFromProjectOrOrganization(search){
            return SearchMMoserUserFromProjectOrOrganization(search, '', 1)
        },
        FormatSendForApprovalErrorMessage(response){
            const rawMessage = typeof response === 'string'
                ? response
                : response?.message || response?.data || ''
            const normalizedMessage = String(rawMessage || '').trim().toLowerCase()

            if(normalizedMessage=='unauthorized'){
                return 'You do not have office access to submit this item for approval.'
            }

            if(normalizedMessage=='boq cannot be sent for approval'){
                return this.GetBoqCannotBeSentReason()
            }

            return rawMessage || this.$i18n.t('Something went wrong')
        },
        HasApprover(){
            return this.approversList.some(u=>Number(u.actionType)==2)
        },
        GetApproverList(){
            return this.approversList.filter((approver)=>Number(approver.actionType)==2)
        },
        IsLoggedInUser(userId){
            const currentUserId = this.userInfo?._id || this.currentUser?._id
            return Boolean(userId && currentUserId && userId==currentUserId)
        },
        IsOnlyApproverLoggedInUser(){
            const approverList = this.GetApproverList()
            return approverList.length==1 && this.IsLoggedInUser(approverList[0]._id)
        },
        GetBoqCannotBeSentReason(){
            if(this.IsOnlyApproverLoggedInUser()){
                const approverName = this.GetDisplayName(this.GetApproverList()[0])
                return `${this.$i18n.t('Cannot send this Detailed Estimate for approval because the only Approver is the logged-in user')}: ${approverName}. ${this.$i18n.t('Add another project team member and set that person to Approve. Keep yourself as Review or remove yourself, then send again.')}`
            }

            if(this.GetApproverList().length>1){
                return this.$i18n.t('Cannot send this Detailed Estimate for approval because more than one user is marked Approve. Keep only one Approver and set the rest to Review.')
            }

            const missingRoleApprover = this.approversList.find((approver)=>!approver.roleId?.roleName)
            if(missingRoleApprover){
                return `${this.$i18n.t('Cannot send this Detailed Estimate for approval because an approver has no role information')}: ${this.GetDisplayName(missingRoleApprover)}. ${this.$i18n.t('Remove and add the user again from Project Team Search.')}`
            }

            return this.$i18n.t('Cannot send this Detailed Estimate for approval. Check that all line items are assigned to sections and that the selected Approver has access to approve this project.')
        },
        async HasDetailedEstimateSections(){
            const sectionData = await GetHleNewBoqCreatorSections(this.boqId).catch(()=>null)
            return Array.isArray(sectionData?.sections) && sectionData.sections.length > 0
        },
        async GetUnassignedLineItemsPreview(){
            const lineItems = await GetHighLevelBoqLineItemsByBits({
                page : 1,
                id : this.boqId,
                costCode : '',
                area : '',
                skip : 0,
                take : 3
            }).catch(()=>[])

            return Array.isArray(lineItems) ? lineItems : []
        },
        FormatLineItemLabel(lineItem){
            const costCode = String(lineItem?.costCode || '').trim()
            const name = String(lineItem?.name || lineItem?.nameCh || lineItem?.description || lineItem?._id || this.$i18n.t('Unnamed line item')).trim()

            return [costCode, name].filter(Boolean).join(' - ')
        },
        FormatUnassignedLineItemsMessage(lineItems){
            const examples = (lineItems || [])
                .map((lineItem)=>this.FormatLineItemLabel(lineItem))
                .filter(Boolean)

            const examplesText = examples.length > 0
                ? ` ${this.$i18n.t('Examples')}: ${examples.join(', ')}.`
                : ''

            return `${this.$i18n.t('Cannot send this Detailed Estimate for approval because some line items are not assigned to a section.')}${examplesText} ${this.$i18n.t('Open Assign Line Items and assign or delete them, then try again.')}`
        },
        GetDefaultActionType(){
            if(this.isExternal){
                return 2
            }

            return this.HasApprover() ? 1 : 2
        },
        async GetLoggedInUser(){
            if(this.userInfo?._id){
                return this.userInfo
            }

            const currentUser = await GetMyUserInfo()
            if(!currentUser || !currentUser._id){
                return null
            }

            this.currentUser = currentUser
            return currentUser
        },
        GetDisplayName(user){
            const fullName = String(user?.fullname || '').trim()
            if(fullName){
                return fullName
            }

            const firstName = String(user?.firstName || '').trim()
            const lastName = String(user?.lastName || '').trim()
            const name = [firstName, lastName].filter(Boolean).join(' ')

            return name || user?.email || user?.name || this.$i18n.t('User')
        },
        GetRoleId(user){
            if(user?.roleId && typeof user.roleId==='object'){
                return {
                    ...user.roleId,
                    roleName : user.roleId.roleName || user.roleName || user.role || ''
                }
            }

            return {
                _id : user?.roleId || user?.role?._id || '',
                roleName : user?.roleName || user?.role?.roleName || user?.role || ''
            }
        },
        BuildApprover(user, actionType){
            return {
                _id : user._id,
                fullname : this.GetDisplayName(user),
                email : user.email || '',
                roleId : this.GetRoleId(user),
                actionType : Number(actionType)
            }
        },
        async AddLoggedInUserAsDefaultApprover(){
            if(this.isExternal || this.approversList.length>0){
                return
            }

            const currentUser = await this.GetLoggedInUser()
            if(!currentUser){
                return
            }

            this.approversList.push(this.BuildApprover(currentUser, 2))
            this.EnsureSingleApprover(currentUser._id)
        },
        EnsureSingleApprover(preferredId){
            const self = this
            if(self.isExternal || self.approversList.length==0){
                return
            }

            let approverIndex = preferredId ? self.approversList.findIndex(u=>u._id==preferredId) : self.approversList.findIndex(u=>Number(u.actionType)==2)
            if(approverIndex==-1){
                approverIndex = 0
            }

            self.approversList.forEach((u,index)=> {
                u.actionType = index==approverIndex ? 2 : 1
            })
        },
        HandleApproverActionChange(item){
            item.actionType = Number(item.actionType)
            if(item.actionType==2){
                this.EnsureSingleApprover(item._id)
                return
            }

            this.EnsureSingleApprover()
        },
        SelectApprover(value){
            this.selectedApprover = value
        },
        AddSelectedApprover(){
            const self = this
            if(!self.CanAddSelectedApprover){
                return
            }

            if(self.isExternal){
                self.AddApproverExternal(self.selectedApprover)
            }else{
                self.AddApprover(self.selectedApprover)
            }
        },
        AddApprover(value){
            const self = this
            const getIndex = self.approversList.findIndex(u=>u._id==value._id)
            if(getIndex==-1){
                self.approversList.push(self.BuildApprover(value, self.GetDefaultActionType()))
                self.EnsureSingleApprover()
            }
            setTimeout(function(){
                self.newTypeName = ''
                self.selectedApprover = null
            },300)
        },
        AddApproverExternal(value){
            const self = this
            const getIndex = self.approversList.findIndex(u=>u._id==value._id)
            if(getIndex==-1){
                self.approversList.push(self.BuildApprover(value, 2))
            }
            setTimeout(function(){
                self.newTypeName = ''
                self.selectedApprover = null
            },300)
        },
        RemoveApprover(id){
            const self = this
            const getIndex = self.approversList.findIndex(u=>u._id==id)
            if(getIndex>-1){
                self.approversList.splice(getIndex,1)
                self.EnsureSingleApprover()
            }
        },
        async SendForApproval(){
            const self = this
            if(self.isSending){
                return
            }

            let willPass = true
            self.isSendClicked = true

            const swalTitleSendForApproval = self.$i18n.t('Send For Approval')
            const swalTitleSendEmail = self.$i18n.t('Approval Module.Sending Email')
            //validate first
            if(!self.emailMessage){
                willPass = false
            }

            if(!self.dateEnd){
                willPass = false
            }
            
            if(self.approversList.length==0){
                willPass = false
                self.$swal(swalTitleSendForApproval,self.$i18n.t('Approval Module.Please put atleast one Approver/Reviewer'),'warning')
            }

            if(self.approversList.length>0 && !self.HasApprover()){
                willPass = false
                self.$swal(swalTitleSendForApproval,'Please keep one user as Approve before sending. The remaining users can stay as Review.','warning')
            }

            if(!willPass){
                return
            }

            if(self.boqId && !(await self.HasDetailedEstimateSections())){
                self.$swal(
                    swalTitleSendForApproval,
                    self.$i18n.t('Please add at least one section before sending this Bill of Quantity for approval.'),
                    'warning'
                )
                return
            }

            const unassignedLineItems = self.boqId ? await self.GetUnassignedLineItemsPreview() : []
            if(unassignedLineItems.length > 0){
                self.$swal(
                    swalTitleSendForApproval,
                    self.FormatUnassignedLineItemsMessage(unassignedLineItems),
                    'warning'
                )
                return
            }
            
            self.isSendClicked = false
            self.isSending = true
            let emailSubject = ''
            let approvalUrl = ''
            const projectDisplayName = self.projectName || self.nameOfItem || '-'
            if(self.boqId){
                const approverList = self.approversList.map((approver)=>({
                    ...approver,
                    actionType : Number(approver.actionType)
                }))
                const response = await ForApprovalHighLevelBoq({
                    boqId : self.boqId,
                    approvers : approverList,
                    approverList : approverList,
                    dateEnd : self.dateEnd
                })
                
                if(!response.processId){
                    self.$swal(swalTitleSendForApproval,self.FormatSendForApprovalErrorMessage(response),'warning')
                    self.sendButtonName = swalTitleSendForApproval
                    self.isSending = false
                    return
                }

                self.sendButtonName = swalTitleSendEmail
                emailSubject = `${projectDisplayName} - Approval of Detailed Estimate`
                if(!self.isExternal){
                    approvalUrl = `${window.location.origin}/high-level-estimates/approval/bill-of-quantities/${response.processId}`
                }else{
                    approvalUrl = `${self.dotnetUrl}/client/approval/hle-boq/${response.processId}`
                }
            }
            else if(self.qtnId){
                const response = await SendHighLevelQuotationForApproval({quotationId : self.qtnId,approvers : self.approversList, dateEnd : self.dateEnd })
                if(!response.processId){
                    self.$swal(swalTitleSendForApproval,self.FormatSendForApprovalErrorMessage(response),'warning')
                    self.sendButtonName = swalTitleSendForApproval
                    self.isSending = false
                    return
                }

                self.sendButtonName = swalTitleSendEmail
                emailSubject = `${projectDisplayName} - Approval of High Level Estimate`
                if(!self.isExternal){
                    approvalUrl = `${window.location.origin}/high-level-estimates/quotation/${response.processId}/approval`
                }else{
                    approvalUrl = `${self.dotnetUrl}/client/approval/hle-budget-estimate/${response.processId}`
                }
            }

            //send the email here
            await UserSendEmailBulk({
                subject : emailSubject,
                message : self.emailMessage,
                link : approvalUrl,
                sendTos : self.approversList
            })

            self.$router.push(self.backUrl)

        },
        BackToItem(){
            const self = this
            self.$router.push(self.backUrl)
        }
    },
    watch :{
        dateEnd(val){
            const self = this
            self.emailMessage = self.emailMessage.replaceAll(self.presetDate, ReformatDate(val))
            self.presetDate = ReformatDate(val)
        },  
    },
    data(){
        const d = new Date()
        d.setDate(d.getDate() + 7)
        const self = this
        return {
            presetReviewers : [],
            dotnetUrl : process.env.VUE_APP_DOTNET_URL,
            boqId : this.$route.query.boqId,
            qtnId : this.$route.query.qtnId,
            isExternal : this.$route.query.isExternal===true || this.$route.query.isExternal==='true',
            dateEnd : ReformatDate(d,'YYYY-MM-DD'),
            presetDate : ReformatDate(d),
            emailMessage : '',
            approversList : [],
            currentUser : null,
            newTypeName : '',
            selectedApprover : null,
            isSendClicked :false,
            isSending : false,
            projectName : '',
            nameOfItem : '',
            backUrl : {},
            sendButtonName : self.$i18n.t('Send For Approval'),
            isApproversTypeDisabled : false
        }
    }
}
</script>
